import { z } from 'zod'
import type { OnboardingListItem } from '@natalan/capehost-contracts'

const DeliveryErrorSchema = z.object({
  code: z.string(),
  message: z.string(),
  /** Set on `invite_cooldown`: how long until another email may go. */
  retry_after_seconds: z.number().int().nonnegative().optional(),
})

export type OnboardingDeliveryError = z.infer<typeof DeliveryErrorSchema>

/**
 * `invite_error` rides on the Start onboarding response but is not in the
 * contracts yet (CAP-1484); read it as an unknown and keep it only when it
 * has the shape.
 */
export function readInviteError(response: Record<string, unknown>): OnboardingDeliveryError | null {
  const parsed = DeliveryErrorSchema.safeParse(response.invite_error)
  return parsed.success ? parsed.data : null
}

/** The `errors[]` a nudge reports for owners it could not reach (CAP-1484). */
export function readNudgeErrors(response: Record<string, unknown>): OnboardingDeliveryError[] {
  const parsed = z.array(DeliveryErrorSchema).safeParse(response.errors)
  return parsed.success ? parsed.data : []
}

const DAY_MS = 24 * 60 * 60 * 1000

/** Whole days from `from` to `to`, rounded down; null when either is missing or unreadable. */
export function daysBetween(from: string | null, to: string | Date | null): number | null {
  if (!from || !to) return null
  const start = Date.parse(from)
  const end = typeof to === 'string' ? Date.parse(to) : to.getTime()
  if (Number.isNaN(start) || Number.isNaN(end)) return null
  return Math.max(0, Math.floor((end - start) / DAY_MS))
}

/**
 * Median days from invite to submit over the properties that have both, in
 * whole days (rounded down, like each row), with how many it covers, for the Onboarding page's footer; null
 * when none has submitted yet, so the page shows no empty metric.
 */
export function medianInviteToSubmitDays(
  items: readonly { invited_at: string | null; submitted_at: string | null }[]
): { days: number; count: number } | null {
  const days = items
    .map(item => daysBetween(item.invited_at, item.submitted_at))
    .filter((d): d is number => d !== null)
    .sort((a, b) => a - b)
  if (days.length === 0) return null
  const mid = Math.floor(days.length / 2)
  const median = days.length % 2 ? days[mid] : (days[mid - 1] + days[mid]) / 2
  return { days: Math.floor(median), count: days.length }
}

/** The latest `nudged` (or, failing that, `invited`) event, for the nudge confirmation. */
export function lastEmailEvent(
  events: readonly { type: string; at: string }[] | null | undefined
): { type: 'nudged' | 'invited'; at: string } | null {
  let latest: { type: 'nudged' | 'invited'; at: string; time: number } | null = null
  for (const event of events ?? []) {
    if (event.type !== 'nudged' && event.type !== 'invited') continue
    const time = Date.parse(event.at)
    if (Number.isNaN(time)) continue
    if (!latest || time > latest.time) latest = { type: event.type, at: event.at, time }
  }
  return latest ? { type: latest.type, at: latest.at } : null
}

export type OnboardingStatusFields = Pick<
  OnboardingListItem,
  'status' | 'sections_complete' | 'sections_total' | 'changed_since_review'
> & { reviewed_at?: string | null }

/** The list's own status in words: invited, started n/12, submitted, reviewed. */
export function onboardingStatusLabel(item: OnboardingStatusFields): string {
  switch (item.status) {
    case 'invited':
      return 'Invited'
    case 'started':
      return `Started ${item.sections_complete}/${item.sections_total}`
    case 'submitted':
      return 'Submitted'
    case 'reviewed':
      return 'Reviewed'
  }
}
